import { IconPencil } from "@tabler/icons-react";
import { type FC, useRef, useState } from "react";

interface ProjectNameEditorProps {
  name: string;
  onChange: (name: string) => void;
  isDisabled?: boolean;
  /**
   * By default the name renders as its own `h2` page title. Hosts that place
   * the editor inside their own heading (e.g. the page header's `h1`) pass
   * `false`, which also switches the wrappers to phrasing content (`span`) so
   * the markup stays valid inside a heading.
   */
  renderAsHeading?: boolean;
}

export const ProjectNameEditor: FC<ProjectNameEditorProps> = ({
  name: initialName,
  onChange,
  isDisabled,
  renderAsHeading = true,
}) => {
  const [name, setName] = useState(initialName);
  const [isEditingProjectName, setIsEditingProjectName] = useState(false);
  const nameInputRef = useRef<HTMLInputElement>(null);

  const Wrapper = renderAsHeading ? "div" : "span";
  const Title = renderAsHeading ? "h2" : "span";

  if (!isEditingProjectName) {
    return (
      <Wrapper
        className={
          renderAsHeading
            ? "group flex h-9 items-center space-x-2"
            : "group inline-flex h-9 items-center space-x-2"
        }
      >
        <Title
          className="max-w-xs cursor-default truncate text-xl font-semibold text-ellipsis text-foreground"
          title={name}
        >
          {name}
        </Title>
        <button
          type="button"
          aria-label="Edit name"
          className="h-fit cursor-pointer rounded-md p-1 hover:bg-slate-200"
          onClick={() => {
            setIsEditingProjectName(true);
            setTimeout(() => {
              nameInputRef.current?.focus();
            });
          }}
        >
          <IconPencil className="h-4 w-4 text-muted-foreground opacity-0 group-focus-within:opacity-100 group-hover:opacity-100" />
        </button>
      </Wrapper>
    );
  }

  return (
    <Wrapper className={renderAsHeading ? "flex h-9" : "inline-flex h-9"}>
      <input
        className="w-80 rounded-md border-0 py-1.5 text-xl leading-7 font-bold text-foreground shadow-xs ring-1 ring-border-strong ring-inset focus:ring-2 focus:ring-indigo-500 focus:ring-inset"
        ref={nameInputRef}
        maxLength={150}
        minLength={0}
        onChange={(evt) => {
          setName(evt.target.value);
        }}
        type="text"
        value={name}
        onKeyDown={(evt) => {
          if (isDisabled) {
            return;
          }

          if (evt.key === "Enter") {
            evt.preventDefault();
            nameInputRef.current?.blur(); // Trigger the onBlur by removing focus from the input
          }
        }}
        onBlur={(evt) => {
          if (isDisabled) {
            return;
          }

          setIsEditingProjectName(false);
          // If the name hasn't changed, don't dispatch an update.
          if (name === initialName) return;

          // If the name isn't valid, just reset it to the original value.
          if (name.length < 2) {
            setName(initialName);
            return;
          }

          onChange(name);
        }}
      />
    </Wrapper>
  );
};
